import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';

const copyStyles = (pipWindow) => {
  [...document.styleSheets].forEach((styleSheet) => {
    try {
      const cssRules = [...styleSheet.cssRules].map((r) => r.cssText).join('');
      const style = document.createElement('style');
      style.textContent = cssRules;
      pipWindow.document.head.appendChild(style);
    } catch (e) {
      if (styleSheet.href) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = styleSheet.href;
        pipWindow.document.head.appendChild(link);
      }
    }
  });
};

export const isPipSupported = () => 'documentPictureInPicture' in window;

export function usePipWindow() {
  const [pipWindow, setPipWindow] = useState(null);

  const openPip = useCallback(async ({ width, height }) => {
    if (!isPipSupported()) return false;
    try {
      const pip = await window.documentPictureInPicture.requestWindow({ width, height });
      copyStyles(pip);
      pip.document.body.style.margin = '0';
      pip.document.body.style.background = '#f9fafb';
      try {
        if (Math.abs(pip.innerHeight - height) > 10 || Math.abs(pip.innerWidth - width) > 10) {
          const chromeW = pip.outerWidth - pip.innerWidth;
          const chromeH = pip.outerHeight - pip.innerHeight;
          pip.resizeTo(width + chromeW, height + chromeH);
        }
      } catch (e) {
        console.debug('PiP initial resize not permitted:', e);
      }
      pip.addEventListener('pagehide', () => setPipWindow(null));
      setPipWindow(pip);
      return true;
    } catch (e) {
      console.debug('PiP open failed, falling back to window.open:', e);
      return false;
    }
  }, []);

  useEffect(() => () => {
    try {
      pipWindow?.close();
    } catch (e) {
      console.debug('PiP close on unmount failed:', e);
    }
  }, [pipWindow]);

  return { pipWindow, openPip };
}

export function PipFrame({ pipWindow, title, barClass = 'bg-emerald-600', children }) {
  const [mini, setMini] = useState(false);
  const contentRef = useRef(null);
  const barRef = useRef(null);
  const savedOuterH = useRef(0);

  // Resize AFTER render so the collapsed bar is measured at its thinnest
  useEffect(() => {
    if (!pipWindow) return;
    try {
      if (mini) {
        const chromeH = Math.max(pipWindow.outerHeight - pipWindow.innerHeight, 0);
        const barH = barRef.current ? barRef.current.offsetHeight : 16;
        pipWindow.resizeTo(pipWindow.outerWidth, barH + chromeH);
      } else if (savedOuterH.current) {
        pipWindow.resizeTo(pipWindow.outerWidth, savedOuterH.current);
      }
    } catch (e) {
      console.debug('PiP resize on toggle not permitted:', e);
    }
  }, [mini, pipWindow]);

  if (!pipWindow) return null;

  const toggle = () => {
    const next = !mini;
    if (next) savedOuterH.current = pipWindow.outerHeight;
    try {
      pipWindow.document.body.style.background = next ? '#111827' : '#f9fafb';
    } catch (e) {
      console.debug('PiP background toggle failed:', e);
    }
    setMini(next);
  };

  return createPortal(
    <div data-testid="pip-container">
      <div
        ref={barRef}
        className={`${mini ? 'bg-gray-900 text-gray-300 px-1.5 py-0 leading-none' : `${barClass} text-white px-3 py-2`} flex items-center justify-between cursor-pointer select-none transition-colors`}
        onClick={toggle}
        data-testid="pip-title-bar"
      >
        <span className={`font-bold truncate ${mini ? 'text-[9px]' : 'text-sm'}`}>{title}</span>
        <button
          onClick={(e) => { e.stopPropagation(); toggle(); }}
          className={`hover:bg-white/20 rounded transition-colors ${mini ? 'p-0' : 'p-1'}`}
          data-testid="pip-toggle-btn"
          title={mini ? 'Expandir' : 'Minimizar'}
        >
          {mini ? <ChevronDown size={10} /> : <ChevronUp size={16} />}
        </button>
      </div>
      <div ref={contentRef} style={{ display: mini ? 'none' : 'block' }} data-testid="pip-content">
        {children}
      </div>
    </div>,
    pipWindow.document.body
  );
}
